import React, { useCallback, useEffect, useRef, useState } from 'react';
import PortalPreview from '../composer/PortalPreview';
import StepPick from '../composer/StepPick';
import { MAX_CLIP_MS, fmtClip } from '../composer/StepTrim';
import TrimCoverSheet from '../composer/TrimCoverSheet';
import useVideoFrames from '../composer/useVideoFrames';
import { safePad } from '../../../utils/safeArea';
import { XIcon, ChevronLeftIcon } from '../canvas/icons';
import { FRAME_DEFAULT } from '../../../utils/mediaFrame';

/**
 * REPLACE CLIP / REPLACE PHOTO inside the Portal page's editor — the composer's PICK panel and
 * its TRIM + COVER sheet in the same 400×820 frame, handing a finished draft back to the editor.
 *
 * Same rules as creating a Portal: the file stays on the device (nothing uploads until SAVE
 * CHANGES), a clip longer than the cap must give up the rest on TRIM, TRIM is never skipped,
 * the cover is chosen from inside the trimmed window, and the ring is aimed at it in the same
 * sheet. A photo is its own cover, so it skips the sheet. Picking "text only" hands back no
 * media at all.
 *
 * `initial` is the draft the editor already holds: reopening on the same clip restores the
 * window that was cut, instead of starting again from the front.
 */
export default function PortalMediaFlow({
  initial = null,
  maxClipMs = MAX_CLIP_MS,
  desktop = false,
  onDone,
  onCancel,
}) {
  const restored = initial && (initial.media?.kind === 'video' || initial.media?.kind === 'photo') && initial.media.localUrl;
  const [media, setMedia] = useState(() => (restored ? initial.media : { kind: 'text', localUrl: null, file: null, name: null }));
  const [trim, setTrim] = useState(() => (restored && initial.trim ? initial.trim : { startMs: 0, endMs: 0 }));
  const [coverPct, setCoverPct] = useState(() => (restored && initial.coverPct != null ? initial.coverPct : 0.3));
  const [coverFocus, setCoverFocus] = useState(() => (restored && initial.coverFocus) || { x: 50, y: 50 });
  const [playSec, setPlaySec] = useState(0);
  const [scrubMs, setScrubMs] = useState(null);
  const [gate, setGate] = useState(false);
  const [gateTab, setGateTab] = useState('trim');
  const [attempted, setAttempted] = useState(false);
  const localUrlRef = useRef(null);
  const handedOff = useRef(false);

  const { frames, durationSec, cover, captureCover, extracting } = useVideoFrames(media.localUrl, media.kind, trim);
  const isVideo = media.kind === 'video';

  // A window that was cut before stays; a fresh file opens on its first 30 seconds.
  useEffect(() => {
    if (!isVideo || !durationSec) return;
    const ms = Math.round(durationSec * 1000);
    setTrim((t) => (t.endMs > t.startMs && t.endMs <= ms + 50 ? t : { startMs: 0, endMs: Math.min(ms, maxClipMs) }));
  }, [durationSec, isVideo, maxClipMs]);

  useEffect(() => { if (isVideo) captureCover(coverPct); }, [coverPct, isVideo, captureCover, trim]);

  // Frame extraction that ran and found nothing is a broken file, not a slow one.
  const wasExtracting = useRef(false);
  useEffect(() => {
    if (wasExtracting.current && !extracting) setAttempted(true);
    wasExtracting.current = extracting;
  }, [extracting]);
  const failed = isVideo && attempted && !extracting && !durationSec;

  // Only a file picked here is ours to revoke; the editor owns the one it handed us.
  useEffect(() => () => {
    if (handedOff.current) return;
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
  }, []);

  const pickFile = useCallback((file) => {
    const kind = file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'photo' : null;
    if (!kind) return;
    if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch { /* gone */ } }
    const url = URL.createObjectURL(file);
    localUrlRef.current = url;
    setMedia({ kind, localUrl: url, file, name: file.name });
    setTrim({ startMs: 0, endMs: 0 });
    setCoverPct(0.3);
    setCoverFocus({ x: 50, y: 50 });
    setAttempted(false);
    setGate(false);
  }, []);

  const pickText = () => {
    handedOff.current = true;
    onDone({ media: { kind: 'text', localUrl: null, file: null, name: null }, trim: null, coverPct: null, coverFocus: null, cover: null });
  };

  const finish = () => {
    handedOff.current = true;
    onDone({
      media,
      trim: isVideo && trim.endMs > trim.startMs ? { startMs: Math.round(trim.startMs), endMs: Math.round(trim.endMs) } : null,
      coverPct: isVideo ? coverPct : null,
      coverFocus: isVideo ? coverFocus : null,
      cover: isVideo ? cover : media.localUrl,
      durationSec,
      frame: { ...FRAME_DEFAULT },
    });
  };

  const canAdvance = media.kind !== 'text' && !!media.localUrl && !failed;
  // A video goes through the gate; a photo is its own cover and is done at once.
  const next = () => { if (!canAdvance) return; if (isVideo) { setGate(true); setGateTab('trim'); } else finish(); };
  const nextLabel = isVideo ? 'NEXT' : 'USE PHOTO';
  const badge = isVideo && durationSec ? `${fmtClip(durationSec)}${durationSec * 1000 > maxClipMs + 50 ? ' · TRIM' : ''}` : null;

  const body = (
    <>
      <PortalPreview
        media={media}
        text=""
        coverPct={coverPct}
        seeking={gate && gateTab === 'cover'}
        scrubMs={gate && gateTab === 'trim' ? scrubMs : null}
        durationSec={durationSec}
        clip={isVideo && trim.endMs > trim.startMs ? trim : null}
        onPlaySec={gate ? setPlaySec : null}
        className="absolute inset-0"
      />
      <div className="absolute inset-x-0 top-0 font-mono text-white" style={{ zIndex: 8, padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) 56px', alignItems: 'center' }}>
          <button type="button" onClick={onCancel} aria-label="Cancel" className="prc-pill" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(0,0,0,.5)', color: '#fff', border: 0, borderRadius: '50%', cursor: 'pointer' }}>
            {gate ? <ChevronLeftIcon /> : <XIcon size={18} />}
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.14em' }}>{media.kind === 'photo' ? 'REPLACE PHOTO' : 'REPLACE CLIP'}</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }}>{gate ? 'TRIM + COVER' : 'PICK'}</span>
          </div>
          {!gate && canAdvance ? (
            <button type="button" onClick={next} style={{ justifySelf: 'end', padding: '8px 0', background: 'transparent', color: '#00FFFF', border: 0, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer' }}>{nextLabel}</button>
          ) : <span />}
        </div>
      </div>

      {!gate && (
        <div className="absolute inset-x-0 bottom-0" style={{ zIndex: 6, background: '#000', padding: safePad('18px 16px 24px') }}>
          <StepPick media={{ ...media, badge }} onPickFile={pickFile} onPickText={pickText} desktop={desktop} />
        </div>
      )}

      {gate && (
        <TrimCoverSheet
          locked
          tab={gateTab}
          onTab={(t) => { setGateTab(t); if (t === 'cover' && !cover) captureCover(coverPct); }}
          onClose={() => {}}
          onConfirm={finish}
          frames={frames}
          durationSec={durationSec}
          trim={trim}
          maxClipMs={maxClipMs}
          playSec={playSec}
          loading={!durationSec && !failed}
          failed={failed}
          onTrim={(nextTrim, { scrubToMs, settled }) => {
            setTrim(nextTrim);
            setPlaySec(scrubToMs / 1000);
            setScrubMs(settled ? null : scrubToMs);
            if (settled) setCoverPct(0.3);
          }}
          coverPct={coverPct}
          onCoverPct={setCoverPct}
          onCoverSettled={() => captureCover(coverPct)}
          cover={cover}
          coverFocus={coverFocus}
          onCoverFocus={setCoverFocus}
        />
      )}
    </>
  );

  if (desktop) {
    return (
      <div className="fixed inset-0 z-[97] flex items-center justify-center" style={{ background: 'rgba(26,26,26,.96)' }} role="dialog" aria-modal="true" data-testid="portal-media-flow">
        <div className="relative overflow-hidden" style={{ width: 400, height: 'min(820px, calc(100vh - 32px))', background: '#000', outline: '2px solid #fff' }}>
          {body}
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[97] overflow-hidden" style={{ background: '#000' }} role="dialog" aria-modal="true" data-testid="portal-media-flow">
      {body}
    </div>
  );
}
